import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Eye, EyeOff, ExternalLink, History, Mail, Pencil, Plus, Trash2, Undo2, Users } from 'lucide-react';
import { AppUser, CategoryKey, Listing, ListingStatus } from '../../../types';
import { CATEGORIES } from '../../../config/categories';
import { useSuperAdminActions } from '../../../hooks/useListingAdmin';
import {
  countByStatus,
  EMPTY_SUPER_FILTER,
  filterSuperDirectory,
  LISTING_STATUSES,
  SuperDirectoryFilter,
} from '../../../utils/superAdmin';
import { pickLang } from '../../../utils/listingDisplay';
import CategoryIcon from '../../directory/CategoryIcon';
import { listingHref } from '../../directory/ListingCard';
import { Checkbox, SelectField, TextField } from '../listing/formFields';
import EmailPanel from './EmailPanel';
import HistoryPanel from './HistoryPanel';
import OwnersPanel from './OwnersPanel';
import { SuperStatusTag } from './SuperStatusTag';
import { formatDate, userLabel } from './superFormat';

const ACTION = 'inline-flex items-center gap-1 min-h-[44px] px-2 text-sm text-ink hover:underline disabled:opacity-40';

/** Every listing, shuls included, with filters (plan 11.2). */
export default function SuperDirectoryTab({ listings, users }: { listings: Listing[]; users: AppUser[] }) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  // ?status=deleted (the link in the "deleted by the owner" email) opens with that filter.
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<SuperDirectoryFilter>(() => {
    const status = searchParams.get('status') as ListingStatus | null;
    return status && LISTING_STATUSES.includes(status) ? { ...EMPTY_SUPER_FILTER, status } : EMPTY_SUPER_FILTER;
  });
  const counts = useMemo(() => countByStatus(listings), [listings]);
  const shown = useMemo(() => filterSuperDirectory(listings, filter, isHebrew), [listings, filter, isHebrew]);

  const set = (patch: Partial<SuperDirectoryFilter>) => setFilter((f) => ({ ...f, ...patch }));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-ash">{t('directory.super.directory.intro')}</p>
        <Link
          to="/directory/add"
          className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-full bg-ink text-paper text-sm font-medium"
        >
          <Plus className="w-4 h-4" aria-hidden />
          {t('directory.super.directory.create')}
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <TextField
          label={t('directory.super.directory.search')}
          value={filter.search}
          onChange={(search) => set({ search })}
        />
        <SelectField<CategoryKey | 'all'>
          label={t('directory.super.directory.category')}
          value={filter.category}
          onChange={(category) => set({ category })}
          options={[
            { value: 'all', label: t('directory.super.directory.all') },
            ...CATEGORIES.map((c) => ({ value: c.key, label: t(`directory.category.${c.key}`) })),
          ]}
        />
        <SelectField<ListingStatus | 'all'>
          label={t('directory.super.directory.status')}
          value={filter.status}
          onChange={(status) => set({ status })}
          options={[
            { value: 'all', label: `${t('directory.super.directory.all')} (${listings.length})` },
            ...LISTING_STATUSES.map((s) => ({ value: s, label: `${t(`directory.listingStatus.${s}`)} (${counts[s]})` })),
          ]}
        />
      </div>
      <Checkbox label={t('directory.super.directory.noOwner')} checked={filter.noOwner} onChange={(noOwner) => set({ noOwner })} />

      <p className="text-xs text-ash">{t('directory.super.directory.count', { count: shown.length })}</p>

      <ul className="space-y-2">
        {shown.map((listing) => (
          <SuperListingRow key={listing.id} listing={listing} users={users} isHebrew={isHebrew} />
        ))}
      </ul>
    </div>
  );
}

type Panel = 'owners' | 'email' | 'history';

function SuperListingRow({ listing, users, isHebrew }: { listing: Listing; users: AppUser[]; isHebrew: boolean }) {
  const { t } = useTranslation();
  const { setVisible, restore, deleteListing, saving } = useSuperAdminActions();
  const [open, setOpen] = useState<Panel | null>(null);
  const toggle = (panel: Panel) => setOpen((o) => (o === panel ? null : panel));
  const [failed, setFailed] = useState(false);
  const sub = 'subcategory' in listing ? listing.subcategory : undefined;
  const owners = (listing.ownerUids ?? []).map((uid) => {
    const u = users.find((x) => x.uid === uid);
    return u ? userLabel(u) : uid;
  });
  const isPublished = listing.status === 'published';
  const isDeleted = listing.status === 'deleted';

  const act = async (fn: () => Promise<unknown>) => {
    setFailed(false);
    try {
      await fn();
    } catch {
      setFailed(true);
    }
  };

  const handleVisibility = () => {
    const key = isPublished ? 'confirmHide' : 'confirmPublish';
    if (window.confirm(t(`directory.super.directory.${key}`))) act(() => setVisible(listing.id, !isPublished));
  };

  const handleRestore = () => {
    if (window.confirm(t('directory.super.directory.confirmRestore'))) act(() => restore(listing));
  };

  const handleDelete = () => {
    if (window.confirm(t('directory.super.directory.confirmDelete', { name: pickLang(listing.nameEn, listing.nameHe, isHebrew) }))) {
      act(() => deleteListing(listing.id));
    }
  };

  return (
    <li className="bg-paper rounded-[16px] border border-ink/10 p-3 space-y-2">
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-10 h-10 rounded-[10px] bg-fog flex items-center justify-center text-ink/70">
          <CategoryIcon category={listing.category} className="w-5 h-5" strokeWidth={1.5} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-ink break-words">{pickLang(listing.nameEn, listing.nameHe, isHebrew)}</p>
          <p className="text-xs text-ash">
            {t(`directory.category.${listing.category}`)}
            {sub ? ` · ${t(`directory.sub.${sub}`)}` : ''}
          </p>
          <p className="text-xs text-ash break-all">
            {owners.length ? (
              <>
                {t('directory.super.owners.title')}: <bdi>{owners.join(', ')}</bdi>
              </>
            ) : (
              t('directory.super.owners.none')
            )}
          </p>
        </div>
        <SuperStatusTag status={listing.status} />
      </div>
      {isDeleted && listing.deletedAt && (
        <p className="text-xs text-danger-600">
          {t('directory.super.directory.deletedOn', { date: formatDate(listing.deletedAt, isHebrew) })}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-x-2 -mx-2">
        <Link to={`/admin?item=${listing.id}`} className={ACTION}>
          <Pencil className="w-4 h-4" aria-hidden />
          {t('directory.super.directory.edit')}
        </Link>
        <Link to={listingHref(listing)} className={ACTION}>
          <ExternalLink className="w-4 h-4" aria-hidden />
          {t(listing.status === 'published' ? 'directory.admin.viewPublic' : 'directory.preview.viewButton')}
        </Link>
        {isDeleted ? (
          <button type="button" disabled={saving} onClick={handleRestore} className={ACTION}>
            <Undo2 className="w-4 h-4" aria-hidden />
            {t('directory.super.directory.restore')}
          </button>
        ) : (
          <button type="button" disabled={saving} onClick={handleVisibility} className={ACTION}>
            {isPublished ? <EyeOff className="w-4 h-4" aria-hidden /> : <Eye className="w-4 h-4" aria-hidden />}
            {isPublished ? t('directory.super.directory.hide') : t('directory.super.directory.publish')}
          </button>
        )}
        <button type="button" onClick={() => toggle('owners')} aria-expanded={open === 'owners'} className={ACTION}>
          <Users className="w-4 h-4" aria-hidden />
          {t('directory.super.directory.ownersAndInvite')}
          {open === 'owners' ? <ChevronUp className="w-4 h-4" aria-hidden /> : <ChevronDown className="w-4 h-4" aria-hidden />}
        </button>
        <button type="button" onClick={() => toggle('email')} aria-expanded={open === 'email'} className={ACTION}>
          <Mail className="w-4 h-4" aria-hidden />
          {t('directory.super.email.button')}
          {open === 'email' ? <ChevronUp className="w-4 h-4" aria-hidden /> : <ChevronDown className="w-4 h-4" aria-hidden />}
        </button>
        <button type="button" onClick={() => toggle('history')} aria-expanded={open === 'history'} className={ACTION}>
          <History className="w-4 h-4" aria-hidden />
          {t('directory.history.button')}
          {open === 'history' ? <ChevronUp className="w-4 h-4" aria-hidden /> : <ChevronDown className="w-4 h-4" aria-hidden />}
        </button>
        <button type="button" disabled={saving} onClick={handleDelete} className={`${ACTION} text-danger-600`}>
          <Trash2 className="w-4 h-4" aria-hidden />
          {t('directory.super.directory.delete')}
        </button>
      </div>
      {failed && <p role="alert" className="text-sm text-danger-600">{t('directory.admin.saveFailed')}</p>}

      {open && (
        <div className="border-t border-ink/10 pt-3">
          {open === 'owners' ? (
            <OwnersPanel listing={listing} users={users} />
          ) : open === 'email' ? (
            <EmailPanel listing={listing} users={users} />
          ) : (
            <HistoryPanel listing={listing} users={users} />
          )}
        </div>
      )}
    </li>
  );
}
